import React from 'react';
import { Container, Box, Typography, Button, Chip, alpha, Grid, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    FilterList as FilterIcon,
    CloudUpload as CloudUploadIcon,
    History as HistoryIcon,
    Security as SecurityIcon,
    CheckCircle as CheckIcon,
    Tune as TuneIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    Handshake as HandshakeIcon,
    Message as MessageIcon,
} from '@mui/icons-material';

const TopluSMSPage = () => {
    const navigate = useNavigate();

    const P = {
        main: '#e74e02ff',
        light: '#d47706ff',
        dark: '#e17e04ff',
    };

    const Feature = ({ text }) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: P.main,
                    mt: 0.9,
                    flexShrink: 0,
                }}
            />
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {text}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ overflow: 'hidden' }}>
            {/* ================= HERO ================= */}
            <Box
                sx={{
                    pt: { xs: 6, md: 10 },
                    pb: { xs: 6, md: 10 },
                    background: `linear-gradient(135deg, ${alpha('#f97316', 0.06)} 0%, #ffffff 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    borderBottom: '1px solid #e5e7eb',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: 600,
                        height: 600,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(P.main, 0.08)} 0%, transparent 70%)`,
                        filter: 'blur(80px)',
                    }}
                />

                <Container maxWidth="lg">
                    <Box sx={{ maxWidth: 720, position: 'relative', zIndex: 1 }}>
                        <Chip
                            label="Toplu SMS Modülü"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                background: alpha(P.main, 0.1),
                                color: P.dark,
                                border: `1px solid ${alpha(P.main, 0.2)}`,
                            }}
                        />

                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                mb: 3,
                                lineHeight: 1.1,
                                color: '#0f172a',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Toplu SMS ve{' '}
                            <Box
                                component="span"
                                sx={{
                                    background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                SMS Bilgilendirme Sistemi
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                color: '#64748b',
                                lineHeight: 1.7,
                                mb: 5,
                            }}
                        >
                            Derneklerin üyeleriyle hızlı, düzenli ve güvenli iletişim kurabilmeleri için
                            gelişmiş bir Toplu SMS ve SMS Bilgilendirme altyapısı. Sistem, dernek yönetim
                            panelinin içinde yer alan yerleşik bir özellik olarak çalışır.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    background: `linear-gradient(135deg, ${P.main} 0%, ${P.dark} 100%)`,
                                    boxShadow: `0 8px 24px ${alpha(P.main, 0.35)}`,
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${P.dark} 0%, #155e75 100%)`,
                                        boxShadow: `0 12px 32px ${alpha(P.main, 0.45)}`,
                                    },
                                }}
                            >
                                Hemen Başlayın
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* 1️⃣ TOPLU SMS PANELİ */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Gelişmiş Toplu SMS 
                                    <br/>
                                    Gönderim Paneli
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                                Yönetim panelinde yer alan Toplu SMS ekranı sayesinde dernek yöneticileri,
                                üyelerine tek bir panel üzerinden kolayca SMS gönderebilir.
                            </Typography>

                            <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 2 }}>
                                Mesaj gönderimleri filtreleme kriterleri:
                            </Typography>

                            <Feature text="İl, İlçe, Şube" />
                            <Feature text="Üyelik durumu" />
                            <Feature text="Yaş ve Cinsiyet" />
                            <Feature text="Yönetici grubu" />
                            <Feature text="Üyelik özellikleri" />

                            <Typography sx={{ color: '#64748b', mt: 3, fontStyle: 'italic' }}>
                                Doğru mesaj, doğru kişilere hızlı şekilde ulaştırılır.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    width: '100%',
                                    maxWidth: 700,
                                    height: { xs: 300, md: 470 },
                                    p: { xs: 2, md: 3 },
                                    borderRadius: 4,
                                    border: '1px solid #e5e7eb',
                                    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                                    mx: 'auto',
                                }}
                            >
                                <Box
                                    component={'img'}
                                    src='/assets/page_images/toplu_sms_panel.webp'
                                    alt="Toplu SMS Paneli"
                                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 2️⃣ TELEFON LİSTESİZ SİSTEM */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fafafa',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <CloudUploadIcon sx={{ fontSize: 64, color: P.main, mb: 2 }} />
                                <Typography sx={{ color: '#64748b', lineHeight: 1.8 }}>
                                    Birçok SMS sisteminde mesaj gönderebilmek için sürekli güncel telefon
                                    listelerinin sisteme yüklenmesi gerekir. Bu durum hem zaman kaybına hem de
                                    veri güncelliği sorunlarına yol açar.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Telefon Listesi Yükleme Zorunluluğunu Ortadan Kaldıran Akıllı Sistem
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                                Böyle bir sürece ihtiyaç yoktur. Üyelerin telefon numaraları zaten sistemde
                                kayıtlı olduğu için SMS gönderimleri doğrudan veritabanı üzerinden
                                gerçekleştirilir.
                            </Typography>

                            <Feature text="Telefon listesi hazırlama gereği yok" />
                            <Feature text="Excel yükleme işlemi yok" />
                            <Feature text="Veri güncelleme sorunu yok" />
                            <Feature text="Yalnızca filtreleme kriterleri seçilir" />
                            <Feature text="SMS gönderimi anında gerçekleşir" />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 3️⃣ KAYIT VE RAPORLAMA */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2rem' },
                                mb: 2,
                                color: '#0f172a',
                            }}
                        >
                            Kayıtlı ve Raporlanabilir SMS Gönderim Geçmişi
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 700, mx: 'auto' }}>
                            Sistem üzerinden gerçekleştirilen tüm SMS gönderimleri kayıt altına alınır.
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {[
                            { 
                                icon: <HistoryIcon />, 
                                title: 'Gönderim Tarihi', 
                                desc: 'Her mesajın tam gönderim zamanı kaydedilir'
                            },
                            { 
                                icon: <FilterIcon />, 
                                title: 'Alıcı Sayısı', 
                                desc: 'Mesajın kaç kişiye ulaştığı detaylı raporlanır'
                            },
                            { 
                                icon: <SecurityIcon />, 
                                title: 'Kredi Kullanımı', 
                                desc: 'Harcanan SMS kredisi şeffaf şekilde görüntülenir'
                            },
                            { 
                                icon: <BusinessIcon />, 
                                title: 'Gönderen Kullanıcı', 
                                desc: 'İşlemi yapan yetkili kişi kaydı tutulur'
                            },
                            { 
                                icon: <CheckIcon />, 
                                title: 'Onay Durumu', 
                                desc: 'Onay mekanizması ve süreç bilgileri görüntülenir'
                            },
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Box
                                    sx={{
                                        p: 3.5,
                                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                                        borderRadius: 3,
                                        border: '1px solid #e5e7eb',
                                        height: '100%',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '3px',
                                            background: `linear-gradient(90deg, ${P.main} 0%, ${P.light} 100%)`,
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                        },
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: `0 12px 32px ${alpha(P.main, 0.15)}`,
                                            borderColor: alpha(P.main, 0.3),
                                            '&::before': {
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                >
                                    <Box 
                                        sx={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2.5,
                                            background: `linear-gradient(135deg, ${alpha(P.main, 0.1)} 0%, ${alpha(P.light, 0.05)} 100%)`,
                                            color: P.main, 
                                            mb: 2,
                                            transition: 'all 0.3s ease',
                                            '& svg': {
                                                fontSize: 28,
                                            }
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography sx={{ 
                                        fontWeight: 700, 
                                        fontSize: '1.05rem',
                                        color: '#0f172a',
                                        mb: 1,
                                        lineHeight: 1.3,
                                    }}>
                                        {item.title}
                                    </Typography>
                                    <Typography sx={{ 
                                        fontSize: '0.88rem',
                                        color: '#64748b',
                                        lineHeight: 1.6,
                                    }}>
                                        {item.desc}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Typography
                        sx={{ textAlign: 'center', color: '#64748b', mt: 4, fontStyle: 'italic' }}
                    >
                        Bu yapı sayesinde SMS gönderimleri şeffaf, denetlenebilir ve raporlanabilir hale
                        gelir.
                    </Typography>
                </Container>
            </Box>

            {/* 4️⃣ GÜVENLİK */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Çipli TC Kimlik Kartı ile Üst Düzey Güvenlik
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                                SMS altyapısı, yalnızca yetkili kişiler tarafından kullanılmasını sağlamak
                                amacıyla üst düzey güvenlik mekanizmaları ile korunur.
                            </Typography>

                            <Typography sx={{ color: '#64748b', mb: 2, lineHeight: 1.8 }}>
                                Sistem içerisinde SMS gönderimi yapılmadan önce, yöneticiler mobil uygulama
                                üzerinden çipli Türkiye Cumhuriyeti Kimlik Kartı ile kimlik doğrulama
                                sürecinden geçebilir.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fafafa',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 2 }}>
                                    Bu doğrulama sayesinde:
                                </Typography>
                                <Feature text="SMS gönderimi yapan kişinin kimliği kesin olarak doğrulanır" />
                                <Feature text="Yetkisiz erişim ve kötüye kullanım ihtimali ortadan kaldırılır" />
                                <Feature text="Kurumsal mesaj gönderim süreçleri tam denetim altına alınır" />
                                <Feature text="SMS gönderimlerinin yalnızca yetkili kişiler tarafından yapılması sağlanır" />
                                <Feature text="Yetkisiz kullanımların önü %100 güvenli olacak şekilde kapatılır" />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 5️⃣ ONAY MEKANİZMASI */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fff',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <CheckIcon sx={{ fontSize: 48, color: P.main, mb: 2 }} />
                                <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
SMS gönderimleri isteğe bağlı olarak onay mekanizmasına bağlanabilir. Böylece yetkilendirdiğiniz diğer sistem kullanıcıları (Örneğin personeller, merkez yöneticileri veya şube yöneticileri) SMS göndermek istediğinde öncelikle belirlenmiş olan süper yetkilinin onayına sunulur.                                </Typography>
                                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                                    Hazırlanan SMS mesajı önce yetkili bir yönetici veya merkez onaycısının
                                    kontrolüne gönderilir.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Onay Mekanizması ile Kontrollü SMS Gönderimi
                                </Typography>
                            </Box>

                            <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                                Bu sayede:
                            </Typography>

                            <Feature text="Kurum adına gönderilen mesajlar kontrol edilir" />
                            <Feature text="Hatalı veya yetkisiz gönderimler engellenir" />
                            <Feature text="Kurumsal iletişim disiplini korunur" />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 6️⃣ DOĞUM GÜNÜ */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2rem' },
                                mb: 2,
                                color: '#0f172a',
                            }}
                        >
                            Otomatik Doğum Günü Mesajları
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 700, mx: 'auto' }}>
                            Üyelerinizin doğum günlerinde, ad ve soyad bilgisi kullanılarak kişiselleştirilmiş
                            SMS mesajları otomatik olarak gönderilebilir.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            maxWidth: 600,
                            mx: 'auto',
                            p: 4,
                            background: '#fafafa',
                            borderRadius: 2,
                            border: '1px solid #e5e7eb',
                        }}
                    >
                        <Feature text="Mesaj içeriği bir kez tanımlanır" />
                        <Feature text="Sistem ilgili tarihte mesajı kendisi gönderir" />
                        <Feature text="Üyelerle olan bağı güçlendirir" />
                        <Feature text="Kurumsal bağlılığı artırır" />
                    </Box>
                </Container>
            </Box>

            {/* 7️⃣ OTOMATİK SMS SERVİSİ */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2rem' },
                                mb: 2,
                                color: '#0f172a',
                            }}
                        >
                            Otomatik SMS Bilgilendirme Servisi
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 700, mx: 'auto' }}>
                            Yalnızca manuel SMS gönderimlerini değil, sistem içinde gerçekleşen birçok işlem
                            için otomatik bilgilendirme mesajları da gönderir.
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {[
                            { icon: <HandshakeIcon />, title: 'Yeni üye kaydı yapıldığında hoş geldiniz mesajı' },
                            {
                                icon: <SecurityIcon />,
                                title: 'Kullanıcı şifre hatırlatma talebinde doğrulama SMS\'i',
                            },
                            { icon: <CheckIcon />, title: 'Online başvuru süreçlerinde gerekli bilgilendirmeler' },
                            { icon: <MessageIcon />, title: 'Form gönderimlerinde SMS bildirimi' },
                            {
                                icon: <BusinessIcon />,
                                title: 'Yeni kullanıcı veya yönetici tanımlandığında erişim bilgileri',
                            },
                        ].map((item, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Box
                                    sx={{
                                        p: 3,
                                        background: '#fff',
                                        borderRadius: 2,
                                        border: '1px solid #e5e7eb',
                                        height: '100%',
                                        display: 'flex',
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ color: P.main, flexShrink: 0 }}>{item.icon}</Box>
                                    <Typography sx={{ color: '#64748b' }}>{item.title}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Typography
                        sx={{ textAlign: 'center', color: '#64748b', mt: 4, fontStyle: 'italic' }}
                    >
                        Tüm bu işlemler arka planda otomatik olarak gerçekleşir.
                    </Typography>
                </Container>
            </Box>

            {/* 8️⃣ MODÜL KONTROLÜ */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Modül Bazlı SMS Kontrolü
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                                SMS bilgilendirme sistemi tamamen kontrol edilebilir bir yapıya sahiptir.
                            </Typography>

                            <Feature text="Hangi modüllerde SMS gönderileceği belirlenir" />
                            <Feature text="Hangi süreçlerde SMS bildirimi yapılacağı seçilir" />
                            <Feature text="İstenmeyen bildirimler kapatılabilir" />
                            <Feature text="Yalnızca gerekli SMS süreçleri aktif bırakılabilir" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fafafa',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <TuneIcon sx={{ fontSize: 64, color: P.main, mb: 2 }} />
                                <Typography sx={{ color: '#64748b', lineHeight: 1.8 }}>
                                    Yönetim paneli üzerinden hangi modüllerde SMS gönderileceği ve hangi
                                    süreçlerde SMS bildirimi yapılacağı ayrı ayrı belirlenebilir.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 9️⃣ KURUMSAL BAŞLIK */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2rem' },
                                mb: 2,
                                color: '#0f172a',
                            }}
                        >
                            Kurumsal Başlık ile SMS Gönderimi
                        </Typography>
                        <Typography
                            sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 700, mx: 'auto', mb: 4 }}
                        >
                            Gönderilen SMS'ler derneğin kurumsal başlığı ile iletilir.
                        </Typography>
                    </Box>

                    <Grid container spacing={3} maxWidth={800} sx={{
                        width: { xs: '100%', md: 'auto' }
                    }} mx="auto">
                        {[
                            { icon: <CheckIcon />, title: 'Mesajların güvenilirliği artar' },
                            { icon: <BusinessIcon />, title: 'Kurumsal kimlik güçlenir' },
                            {
                                icon: <HandshakeIcon />,
                                title: 'Üyeler mesajın derneklerinden geldiğini kolayca anlayabilir',
                            },
                        ].map((item, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Box
                                    sx={{
                                        p: 3,
                                        background: '#fff',
                                        borderRadius: 2,
                                        border: '1px solid #e5e7eb',
                                        textAlign: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <Box sx={{ color: P.main, mb: 1.5 }}>{item.icon}</Box>
                                    <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔟 ZAMAN PLANLI */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fafafa',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <ScheduleIcon sx={{ fontSize: 64, color: P.main, mb: 2 }} />
                                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 2 }}>
                                    Özel günlerde gönderilecek mesajlar önceden planlanabilir:
                                </Typography>
                                <Feature text="Bayram mesajları" />
                                <Feature text="Genel duyurular" />
                                <Feature text="Etkinlik bilgilendirmeleri" />
                                <Typography sx={{ color: '#64748b', mt: 2, fontStyle: 'italic' }}>
                                    Sistem, belirlenen tarihte mesajları otomatik olarak gönderir.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.75rem', md: '2rem' },
                                        color: '#0f172a',
                                    }}
                                >
                                    Zaman Planlı SMS Gönderimi
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#64748b', lineHeight: 1.8 }}>
                                Özel günlerde gönderilecek mesajlar önceden hazırlanarak ileri tarihli gönderim
                                planlanabilir. Bayram mesajları, genel duyurular ve etkinlik bilgilendirmeleri
                                belirlenen tarihte otomatik olarak gönderilir.
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 1️⃣1️⃣ AİDAT SMS */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2rem' },
                                mb: 2,
                                color: '#0f172a',
                            }}
                        >
                            Aidat Bildirimi ve Borç Hatırlatma SMS Servisi
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '1.1rem', maxWidth: 700, mx: 'auto' }}>
                            Derneklerin en önemli süreçlerinden biri olan aidat yönetimini SMS bilgilendirme
                            altyapısı ile destekler.
                        </Typography>
                    </Box>

                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fff',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    height: '100%',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 2 }}>
                                    Sistem otomatik SMS gönderir:
                                </Typography>
                                <Feature text="Aidat borcu oluştuğunda" />
                                <Feature text="Aidat ödeme zamanı geldiğinde" />
                                <Feature text="Aidat gecikmesi yaşandığında" />
                                <Feature text="Aidat ödeme hatırlatma süreçlerinde" />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 4,
                                    background: '#fff',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    height: '100%',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 2 }}>
                                    Bu özellik sayesinde:
                                </Typography>
                                <Feature text="Aidat tahsilat oranı artar" />
                                <Feature text="Üyeler ödeme durumlarını düzenli takip edebilir" />
                                <Feature text="Dernek yönetiminin manuel hatırlatma yapmasına gerek kalmaz" />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 1️⃣2️⃣ CTA */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    background: '#f8fafc',
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 50%, ${P.dark} 100%)`,
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 4,
                            py: { xs: 6, md: 8 },
                            px: { xs: 3, md: 6 },
                        }}
                    >
                        {/* Arka plan deseni */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.15,
                                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                                backgroundSize: '30px 30px'
                            }}
                        />

                        {/* Decorative circles */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-10%',
                                right: '-5%',
                                width: 400,
                                height: 400,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                filter: 'blur(60px)',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '-10%',
                                left: '-5%',
                                width: 300,
                                height: 300,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.08)',
                                filter: 'blur(50px)',
                            }}
                        />

                        <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                                    fontWeight: 900,
                                    mb: 3,
                                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                Üyelerinizle Güçlü İletişim Kurun
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                                    mb: 5,
                                    opacity: 0.95,
                                    maxWidth: '680px',
                                    mx: 'auto',
                                    lineHeight: 1.7,
                                    color: 'rgba(255,255,255,0.9)'
                                }}
                            >
                                SMS altyapısı sayesinde dernekler üyeleriyle düzenli iletişim kurabilir,
                                bilgilendirmeleri anında iletebilir ve tüm iletişim süreçlerini tek bir platform
                                üzerinden yönetebilir.
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 3,
                                    justifyContent: 'center'
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/demo')}
                                    sx={{
                                        bgcolor: 'white',
                                        color: P.main,
                                        px: 5,
                                        py: 2,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            bgcolor: '#fafafa',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                                        }
                                    }}
                                >
                                    Hemen Başvurun
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate('/iletisim')}
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        px: 5,
                                        py: 2,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        borderWidth: 2,
                                        backdropFilter: 'blur(10px)',
                                        background: 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            borderColor: 'white',
                                            borderWidth: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                        }
                                    }}
                                >
                                    İletişime Geçin
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default TopluSMSPage;

